import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    ACCOUNTS_QUERY_PARAM,
    KEYWORD_QUERY_PARAM,
    OFFSET_QUERY_PARAM,
    SOCIAL_MEDIA_SERVICE_QUERY_PARAM,
    TEAMS_QUERY_PARAM,
    decodeIdsQueryParam,
    encodeIdsQueryParam
} from '../models/QueryParameters'

export type AdminPostSearchChanges = {
    teamIds?: number[]
    accountIds?: number[]
    socialMediaServiceIds?: number[]
    keyword?: string
    offset?: number
}

export type AdminPostSearchParams = {
    teamIds: number[]
    accountIds: number[]
    socialMediaServiceIds: number[]
    keyword: string
    offset: number
    /** True when any filter narrows the list. Paging alone does not count. */
    filtered: boolean
    /**
     * Writes the given changes to the URL, leaving other keys as they are. Any
     * change goes back to the first page unless an offset is given, since a
     * filter change is a new list. Pass `replace` for changes that should not
     * add a history entry, such as each debounced keystroke in the search box,
     * so Back steps over the typing rather than through it.
     */
    update: (changes: AdminPostSearchChanges, options?: { replace?: boolean }) => void
}

/**
 * The admin Posts page's filters and page offset, read from and written to the
 * URL so a view can be shared or bookmarked and Back undoes a filter. Mirrors
 * `useContentSearchParams` for the content search page, without the date,
 * classifier and read-receipt parameters that page needs.
 *
 * The id arrays are memoised on the search string because they feed Apollo
 * query variables and effect dependencies; a fresh array each render would
 * refetch and re-run them.
 */
export default function useAdminPostSearchParams(): AdminPostSearchParams {
    const [searchParams, setSearchParams] = useSearchParams()

    const teamIds = useMemo(
        () => decodeIdsQueryParam(searchParams.get(TEAMS_QUERY_PARAM) || ''),
        [searchParams]
    )
    const accountIds = useMemo(
        () => decodeIdsQueryParam(searchParams.get(ACCOUNTS_QUERY_PARAM) || ''),
        [searchParams]
    )
    const socialMediaServiceIds = useMemo(
        () => decodeIdsQueryParam(searchParams.get(SOCIAL_MEDIA_SERVICE_QUERY_PARAM) || ''),
        [searchParams]
    )
    const keyword = searchParams.get(KEYWORD_QUERY_PARAM) || ''
    const offset = Math.max(0, parseInt(searchParams.get(OFFSET_QUERY_PARAM) || '0', 10) || 0)

    const filtered =
        teamIds.length > 0 ||
        accountIds.length > 0 ||
        socialMediaServiceIds.length > 0 ||
        keyword !== ''

    const update = useCallback(
        (changes: AdminPostSearchChanges, options: { replace?: boolean } = {}) => {
            const next = new URLSearchParams(searchParams)
            const setIds = (key: string, ids?: number[]) => {
                if (ids === undefined) return
                if (ids.length > 0) next.set(key, encodeIdsQueryParam(ids))
                else next.delete(key)
            }
            setIds(TEAMS_QUERY_PARAM, changes.teamIds)
            setIds(ACCOUNTS_QUERY_PARAM, changes.accountIds)
            setIds(SOCIAL_MEDIA_SERVICE_QUERY_PARAM, changes.socialMediaServiceIds)
            if (changes.keyword !== undefined) {
                if (changes.keyword.trim()) next.set(KEYWORD_QUERY_PARAM, changes.keyword.trim())
                else next.delete(KEYWORD_QUERY_PARAM)
            }
            const nextOffset = changes.offset ?? 0
            if (nextOffset > 0) next.set(OFFSET_QUERY_PARAM, String(nextOffset))
            else next.delete(OFFSET_QUERY_PARAM)
            setSearchParams(next, { replace: options.replace ?? false })
        },
        [searchParams, setSearchParams]
    )

    return { teamIds, accountIds, socialMediaServiceIds, keyword, offset, filtered, update }
}
